import { useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { casinos } from '../../mocks/casinos';
import { getPlayLinkByBrand, trackRegisterClickByBrand } from '../../config/playLinks';
import CasinoReviewPanel from '../../components/feature/CasinoReviewPanel';

const editorInfo = {
  name: 'Charlotte Mitchell',
  title: 'Senior Casino Expert',
  avatar: 'https://readdy.ai/api/search-image?query=professional%20female%20casino%20expert%20reviewer%20headshot%20portrait%20with%20clean%20white%20background%20friendly%20smile%20business%20casual%20attire%20confident%20expression%20studio%20lighting%20high%20quality%20professional%20photography&width=120&height=120&seq=editor-sarah-mitchell&orientation=squarish',
  intro: 'With over 12 years of experience reviewing online casinos, I personally test every platform on this list. These are my hand-picked recommendations based on real gameplay, customer service interactions, and thorough security checks.',
  experience: '12+ years',
  reviewsCount: '500+'
};

export default function MyTopPicks() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [expandedReview, setExpandedReview] = useState<string | null>(null);
  const filters = ['All', 'New', 'Best Bonus', 'Fast Payout'];
  const filterCasinoNames: Record<string, string[]> = {
    New: ['ipay9vip', 'mrboom9'],
    'Best Bonus': ['bigpay77', 'me99', 'gucci9', 'spinaroo', 'mrbean9', 'queen13'],
    'Fast Payout': ['ipay9', 'kingbet9', 'rolex9', 'micky9', 'winnie777', 'bybid9'],
  };

  const getCasinoKey = (name: string) =>
    name.toLowerCase().replace(' online casino', '').replace(/\s+/g, '');

  const toggleReview = (casinoName: string) => {
    setExpandedReview(expandedReview === casinoName ? null : casinoName);
  };

  const topPickCasinos =
    activeFilter === 'All'
      ? casinos
      : casinos
          .filter((casino) => filterCasinoNames[activeFilter]?.includes(getCasinoKey(casino.name)))
          .sort((a, b) => {
            const selected = filterCasinoNames[activeFilter] ?? [];
            return selected.indexOf(getCasinoKey(a.name)) - selected.indexOf(getCasinoKey(b.name));
          });

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={`full-${i}`} className="ri-star-fill text-yellow-400"></i>);
    }
    if (hasHalfStar) {
      stars.push(<i key="half" className="ri-star-half-fill text-yellow-400"></i>);
    }
    const remaining = 5 - Math.ceil(rating);
    for (let i = 0; i < remaining; i++) {
      stars.push(<i key={`empty-${i}`} className="ri-star-line text-gray-300"></i>);
    }
    return stars;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Banner with Editor Info */}
      <div className="bg-gradient-to-br from-[#1a1040] via-[#2d1b69] to-[#5b21b6] text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm text-purple-200 mb-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-white">Editor's Top Picks</span>
          </div>

          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-8">
            {/* Editor Avatar */}
            <div className="flex-shrink-0">
              <div className="relative">
                <img 
                  src={editorInfo.avatar}
                  alt={editorInfo.name}
                  className="w-32 h-32 rounded-full border-4 border-orange-400 object-cover"
                />
                <div className="absolute -bottom-2 -right-2 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                  Editor's Choice
                </div>
              </div>
            </div>

            {/* Editor Info & Title */}
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                <h1 className="text-3xl lg:text-5xl font-bold">Hey mate, My Top Picks</h1>
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className="ri-star-fill text-3xl text-orange-400"></i>
                </div>
              </div>
              
              <div className="flex items-center space-x-4 mb-4">
                <div>
                  <p className="text-xl font-semibold text-orange-300">{editorInfo.name}</p>
                  <p className="text-sm text-purple-200">{editorInfo.title}</p>
                </div>
                <div className="h-10 w-px bg-purple-400"></div>
                <div className="flex items-center space-x-4 text-sm">
                  <div>
                    <p className="text-purple-200">Experience</p>
                    <p className="font-semibold">{editorInfo.experience}</p>
                  </div>
                  <div>
                    <p className="text-purple-200">Reviews</p>
                    <p className="font-semibold">{editorInfo.reviewsCount}</p>
                  </div>
                </div>
              </div>

              <p className="text-purple-100 leading-relaxed max-w-3xl">
                {editorInfo.intro}
              </p>

              <div className="flex items-center space-x-2 mt-4 text-sm text-purple-200">
                <i className="ri-calendar-line"></i>
                <span>Last updated: January 2026</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Column */}
          <div className="flex-1">
            <h2 className="md:text-3xl text-2xl font-bold text-gray-900 mb-5">Top Pick AUS Casino of the Month</h2>

            {/* Filter Tabs */}
            <div className="mb-8 border-b border-gray-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="flex min-w-max gap-2 sm:gap-3">
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`px-4 sm:px-5 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                      activeFilter === filter
                        ? 'text-green-600 border-b-2 border-green-600'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6 text-gray-600">
              Showing {topPickCasinos.length} casino{topPickCasinos.length !== 1 ? 's' : ''}
            </div>

            {/* Casino Cards */}
            <div className="space-y-6">
              {topPickCasinos.map((casino) => (
                <div key={casino.rank} className="space-y-4">
                  <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-lg transition-shadow">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xl">#{casino.rank}</span>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <div className="w-full sm:w-28 h-40 sm:h-28 bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                        <img src={casino.logo} alt={casino.name} className="w-full h-full object-cover" />
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <div>
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{casino.name}</h3>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex gap-0.5">{renderStars(casino.rating)}</div>
                            <span className="text-sm font-semibold text-gray-900">{casino.rating}</span>
                          </div>
                        </div>
                        {casino.featured && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap">
                            Featured
                          </span>
                        )}
                      </div>

                      <p className="text-gray-600 text-sm mb-3">{casino.description}</p>

                      <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 mb-3">
                        <i className="ri-gift-line text-green-600"></i>
                        <span className="text-green-700 font-semibold text-sm">{casino.bonus}</span>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1.5">
                          <i className="ri-gamepad-line text-gray-400"></i>
                          <span>{casino.games} Games</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-money-dollar-circle-line text-gray-400"></i>
                          <span>Min Deposit: {casino.minDeposit}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <i className="ri-time-line text-gray-400"></i>
                          <span>Payout: {casino.payoutSpeed}</span>
                        </div>
                      </div>

                      <p className="text-xs text-gray-500">T&amp;Cs Apply. 18+ Only. KoalaBet.win</p>
                    </div>

                      <div className="w-full sm:w-auto flex sm:flex-col gap-2">
                        <a
                          href={getPlayLinkByBrand(getCasinoKey(casino.name))}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackRegisterClickByBrand(getCasinoKey(casino.name))}
                          className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 min-h-[44px] rounded-lg transition-colors whitespace-nowrap text-center"
                        >
                          Visit Casino
                        </a>
                        <button
                          type="button"
                          onClick={() => toggleReview(casino.name)}
                          className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-900 font-semibold px-6 py-3 min-h-[44px] rounded-lg border-2 border-gray-300 transition-colors whitespace-nowrap text-center"
                        >
                          {expandedReview === casino.name ? 'Hide Review' : 'Read Review'}
                        </button>
                      </div>
                    </div>
                  </div>

                  <CasinoReviewPanel
                    casino={casino}
                    isOpen={expandedReview === casino.name}
                    onClose={() => setExpandedReview(null)}
                  />
                </div>
              ))}
            </div>

            {/* Editor Methodology */}
            <div className="mt-12 bg-gradient-to-r from-purple-50 to-orange-50 border border-purple-200 rounded-lg p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-purple-600 rounded-full flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">How I Select My Top Picks</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Every casino on this list has been personally tested by me. I don't just read reviews or check licenses—I create accounts, make deposits, play games, test withdrawals, and contact customer support. My recommendations are based on real experiences, not marketing materials.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-gray-900">Real Money Testing</p>
                        <p className="text-sm text-gray-600">I deposit and play with my own money</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-gray-900">Withdrawal Verification</p>
                        <p className="text-sm text-gray-600">I test payout speed and processes</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-gray-900">Support Quality Check</p>
                        <p className="text-sm text-gray-600">I contact support with real questions</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-xl text-green-600 mt-0.5"></i>
                      <div>
                        <p className="font-semibold text-gray-900">Terms Review</p>
                        <p className="text-sm text-gray-600">I read all bonus terms and conditions</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:w-80 space-y-6">
            {/* Editor's Note */}
            <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-3">
                <i className="ri-quill-pen-line text-xl text-orange-600"></i>
                <h3 className="text-lg font-bold text-gray-900">Editor's Note</h3>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                These aren't just the highest-rated casinos—they're the ones I personally use and recommend to friends and family. Each has been thoroughly vetted for safety, fairness, and player satisfaction.
              </p>
              <div className="flex items-center space-x-3">
                <img 
                  src={editorInfo.avatar}
                  alt={editorInfo.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900">{editorInfo.name}</p>
                  <p className="text-xs text-gray-600">{editorInfo.title}</p>
                </div>
              </div>
            </div>

            {/* Selection Criteria */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">My Selection Criteria</h3>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-purple-100 rounded-full flex-shrink-0">
                    <i className="ri-shield-check-line text-purple-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Safety First</p>
                    <p className="text-xs text-gray-600">Valid licenses and security</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Fair Bonuses</p>
                    <p className="text-xs text-gray-600">Reasonable wagering terms</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
                    <i className="ri-gamepad-line text-orange-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Game Quality</p>
                    <p className="text-xs text-gray-600">Top providers and variety</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                    <i className="ri-customer-service-2-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Support Quality</p>
                    <p className="text-xs text-gray-600">Responsive and helpful</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-purple-600 to-purple-800 text-white rounded-lg p-6">
              <h3 className="text-lg font-bold mb-4">My Testing Stats</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-purple-200">Casinos Tested</span>
                    <span className="font-bold">500+</span>
                  </div>
                  <div className="h-2 bg-purple-900 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-400" style={{ width: '100%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-purple-200">Hours Played</span>
                    <span className="font-bold">10,000+</span>
                  </div>
                  <div className="h-2 bg-purple-900 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-400" style={{ width: '95%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-purple-200">Withdrawals Tested</span>
                    <span className="font-bold">200+</span>
                  </div>
                  <div className="h-2 bg-purple-900 rounded-full overflow-hidden">
                    <div className="h-full bg-orange-400" style={{ width: '80%' }}></div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}